/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { DownOutlined } from '@ant-design/icons';
import { Grid } from 'antd';
import { useNavigate } from 'react-router-dom';

import { CountLabel } from '@/components/CountLabel';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Dropdown, MenuProps } from '@/primitives/Dropdown';
import { Segmented, SegmentedValue } from '@/primitives/Segmented';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import './NodesPageTabs.css';
import { NodesPageTab, NodesPageTabsProps } from './types';

const { useBreakpoint } = Grid;

export function NodesPageTabs({ activeTab, counts, loading, onTabChange }: NodesPageTabsProps) {
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const { t } = useMimicTranslation('fleets');
  const hasResolvedBreakpoint = Object.values(screens).some(Boolean);
  const isCompactTabs = hasResolvedBreakpoint && screens.md === false;

  const handleTabChange = (tab: NodesPageTab) => {
    if (tab === NodesPageTab.FLEETS && activeTab !== NodesPageTab.FLEETS) {
      navigate(`/tenants/${tenantId}/fleet-management`);
    } else if (tab !== NodesPageTab.FLEETS && activeTab === NodesPageTab.FLEETS) {
      navigate(`/tenants/${tenantId}/nodes?nodeFilter=${tab}`);
    } else if (onTabChange) {
      onTabChange(tab);
    }
  };

  const handleChange = (value: SegmentedValue) => {
    handleTabChange(value as NodesPageTab);
  };

  const tabOptions = [
    {
      label: (
        <CountLabel
          text={t('tabs.configured')}
          count={counts.configured}
          loading={loading.configured}
          data-testid="segment-configured"
        />
      ),
      value: NodesPageTab.CONFIGURED,
    },
    {
      label: (
        <CountLabel
          text={t('tabs.fleets')}
          count={counts.fleets}
          loading={loading.fleets}
          data-testid="segment-fleets"
        />
      ),
      value: NodesPageTab.FLEETS,
    },
    {
      label: (
        <CountLabel
          text={t('tabs.pending')}
          count={counts.pending}
          loading={loading.pending}
          data-testid="segment-pending"
        />
      ),
      value: NodesPageTab.PENDING,
    },
    {
      label: (
        <CountLabel
          text={t('tabs.archived')}
          count={counts.archived}
          loading={loading.archived}
          data-testid="segment-archived"
        />
      ),
      value: NodesPageTab.ARCHIVED,
    },
  ];
  const activeOption = tabOptions.find((option) => option.value === activeTab);
  const menu: MenuProps = {
    items: tabOptions.map((option) => ({
      key: option.value,
      label: option.label,
    })),
    onClick: ({ key }) => handleTabChange(key as NodesPageTab),
    selectable: true,
    selectedKeys: [activeTab],
  };

  if (isCompactTabs) {
    return (
      <div className="nodes-page-tabs nodes-page-tabs-compact" data-testid="nodes-page-switcher">
        <Dropdown menu={menu}>
          <Button type="default" block style={{ height: '44px' }}>
            {activeOption?.label} <DownOutlined style={{ fontSize: '10px' }} />
          </Button>
        </Dropdown>
      </div>
    );
  }

  return (
    <div className="nodes-page-tabs">
      <Segmented
        dataTestId="nodes-page-switcher"
        value={activeTab}
        onChange={handleChange}
        block={!screens.sm}
        options={tabOptions}
      />
    </div>
  );
}
